import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FolderKanban, CheckCircle2, Clock, AlertTriangle, 
  Plus, MoreVertical, Sparkles, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useProjectStore, useTaskStore, useReportStore, useUIStore, useAuthStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { projects, fetchProjects, setCurrentProject } = useProjectStore();
  const { tasks, fetchTasks } = useTaskStore();
  const { summary, fetchSummary, insights, fetchInsights } = useReportStore();
  const { language } = useUIStore();
  const { user } = useAuthStore();
  const { t } = useTranslation(language);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchProjects(),
          fetchTasks(),
          fetchSummary(),
        ]);
        // Fetch insights separately (can be slower)
        fetchInsights().catch(console.error);
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const getHealthColor = (health) => {
    switch (health) {
      case 'green': return 'text-chart-3';
      case 'yellow': return 'text-chart-4';
      case 'red': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getHealthBg = (health) => {
    switch (health) {
      case 'green': return 'bg-chart-3/20';
      case 'yellow': return 'bg-chart-4/20';
      case 'red': return 'bg-destructive/20';
      default: return 'bg-muted';
    }
  };

  const getHealthText = (health) => {
    switch (health) {
      case 'green': return t('on_track');
      case 'yellow': return t('needs_attention');
      case 'red': return t('at_risk');
      default: return '';
    }
  };

  const recentTasks = tasks.slice(0, 5);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">
            {language === 'hi' ? `नमस्ते, ${user?.name}` : `Hello, ${user?.name}`}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'hi' ? 'आज आपका अवलोकन' : "Here's your overview for today"}
          </p>
        </div>
        <Button 
          onClick={() => navigate('/app/projects')} 
          className="glow-primary"
          data-testid="new-project-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('new_project')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('total_projects')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{summary?.projects || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <FolderKanban className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('total_tasks')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{summary?.tasks?.total || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-accent/20 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('completed_this_week')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{summary?.completed_this_week || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-chart-3/20 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-chart-3" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('productivity_score')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{summary?.productivity_score || 0}%</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-chart-4/20 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-chart-4" />
                </div>
              </div>
              <Progress value={summary?.productivity_score || 0} className="mt-3 h-2" />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Projects List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2"
        >
          <Card className="glass h-full">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">{t('projects')}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/app/projects')}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <div className="text-center py-12">
                  <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('no_projects')}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t('create_first_project')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setCurrentProject(project);
                        navigate('/app/projects');
                      }}
                      data-testid={`project-${project.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-lg ${getHealthBg(project.health)} flex items-center justify-center`}>
                          <FolderKanban className={`h-5 w-5 ${getHealthColor(project.health)}`} />
                        </div>
                        <div>
                          <p className="font-medium">{project.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {project.task_counts?.todo || 0} {t('todo')} · {project.task_counts?.in_progress || 0} {t('in_progress')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${getHealthBg(project.health)} ${getHealthColor(project.health)}`}>
                          {getHealthText(project.health)}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate('/app/projects')}>
                              {t('edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-destructive">
                              {t('delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* AI Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="glass h-full">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {t('ai_insights')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {language === 'hi' ? 'अभी तक कोई इनसाइट नहीं' : 'No insights yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {insights.map((insight, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border ${
                        insight.type === 'risk' 
                          ? 'border-destructive/30 bg-destructive/5' 
                          : insight.type === 'bottleneck'
                          ? 'border-chart-4/30 bg-chart-4/5'
                          : 'border-primary/30 bg-primary/5'
                      }`}
                    >
                      <p className="font-medium text-sm">{insight.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Recent Tasks */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card className="glass">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">{t('tasks')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/tasks')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentTasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {language === 'hi' ? 'कोई टास्क नहीं' : 'No tasks yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 transition-colors"
                    data-testid={`task-${task.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-2 w-2 rounded-full ${
                        task.status === 'done' ? 'bg-chart-3' : 
                        task.status === 'in_progress' ? 'bg-chart-4' : 'bg-muted-foreground'
                      }`} />
                      <span className={task.status === 'done' ? 'line-through text-muted-foreground' : ''}>
                        {task.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        task.priority === 'high' ? 'bg-destructive/20 text-destructive' :
                        task.priority === 'medium' ? 'bg-chart-4/20 text-chart-4' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {t(task.priority)}
                      </span>
                      {task.due_date && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
